/**
 * Description Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for description/textarea columns.
 * Inline editing uses a single-line input (Enter to save) for better UX.
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { DescriptionCellContainer, DescriptionText } from '../../tableStyles';
import { TableTooltip } from '../../TableTooltip';
import { EditableDescriptionCell } from './EditableDescriptionCell';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import { getCellStringValue } from '../cellValue';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Description read-only renderer - displays text in a cell container
 * Uses getValue() to support nested accessors
 */
export const descriptionReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    const displayValue = String(value ?? '');
    return (
      <TableTooltip title={displayValue}>
        <DescriptionCellContainer>
          <DescriptionText>{displayValue}</DescriptionText>
        </DescriptionCellContainer>
      </TableTooltip>
    );
  },
});

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Description editable renderer - unified pattern with single-line input
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 *
 * Note: Uses CellInput (single-line) for inline editing so Enter saves the value.
 * Form modals use a multiline textarea via renderDescriptionField.
 */
export const descriptionEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellStringValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      if (!isRowEditable) {
        return (
          <TableTooltip title={value}>
            <DescriptionCellContainer>
              <DescriptionText>{value}</DescriptionText>
            </DescriptionCellContainer>
          </TableTooltip>
        );
      }

      return (
        <EditableDescriptionCell
          context={context}
          value={value || ''}
          onUpdate={newValue => {
            if (col.onUpdate) {
              return col.onUpdate(context.row.original, {
                [col.id]: newValue,
              } as Partial<T>);
            }
          }}
          placeholder={col.placeholder}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const descriptionReadOnly: ColumnRenderer = descriptionReadOnlyRenderer;

/** Editable renderer with proper typing */
export const descriptionEditable: ColumnRenderer = descriptionEditableRenderer;
